export type InventoryMapPatchPath = Array<string | number>
// `[path, value]` sets a value; `[path, null, 'delete']` removes a missing key.
export type InventoryMapPatchOp = [path: InventoryMapPatchPath, value: unknown, mode?: 'delete']

export interface InventoryMapPatch {
  eventId: string
  baseVersion: string
  version: string
  ops: InventoryMapPatchOp[]
  occurredAt: string
}

const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype']

function applyOp(root: Record<string | number, unknown>, [path, value, mode]: InventoryMapPatchOp) {
  if (!path.length || path.some(key => FORBIDDEN_KEYS.includes(String(key)))) throw new Error('INVALID_PATCH_PATH')
  let target: unknown = root
  for (const key of path.slice(0, -1)) {
    target = typeof target === 'object' && target !== null
      ? (target as Record<string | number, unknown>)[key]
      : undefined
  }
  if (typeof target !== 'object' || target === null) throw new Error('INVALID_PATCH_PATH')
  const container = target as Record<string | number, unknown>
  const last = path[path.length - 1]!
  if (mode !== 'delete') {
    container[last] = value
    return
  }
  if (Array.isArray(container)) throw new Error('INVALID_PATCH_PATH')
  delete container[last]
}

/**
 * Applies a server patch to a clone of the raw map DTO. Returns null when the
 * patch does not start from the DTO's `contentVersion` or cannot be applied,
 * so callers fall back to a full reload.
 */
export function applyInventoryMapPatch<T extends { contentVersion?: string }>(
  raw: T,
  patch: InventoryMapPatch
): T | null {
  // An HTTP load can land before the patch for the same change: already current.
  if (raw.contentVersion === patch.version) return raw
  if (raw.contentVersion !== patch.baseVersion) return null
  try {
    const next = structuredClone(raw) as unknown as Record<string | number, unknown>
    for (const op of patch.ops) applyOp(next, op)
    next.contentVersion = patch.version
    return next as unknown as T
  } catch {
    return null
  }
}
